// ENVIRONMENT
import { environment } from '../../environments/environment';

// ANGULAR
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Injectable } from '@angular/core';

// RxJS 6
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

// MODELS
import {
  IWHToolDTO,
  IWHToolsPreviewDTO,
  IWHPageableToolDTO,
  FLOW_GATEWAY,
  WHFileUploadDataService,
} from '@workheld/workheld-shared-lib';

@Injectable()
export class ToolAsyncService {
  constructor(
    private httpClient: HttpClient,
    private fileUploadDataService: WHFileUploadDataService,
  ) {}

  public initToolFormGroup(): FormGroup {
    return new FormBuilder().group({
      id: [null],
      extId: [null, Validators.maxLength(100)],
      toolStatus: [null],
      name: [null, [Validators.required]],
      description: [null],
      serialNumber: [null, Validators.maxLength(100)],
    });
  }

  public removeImageFile(toolId: string): Observable<any> {
    return this.httpClient.delete<any>(
      `${environment.apiUrl + FLOW_GATEWAY}/tools/${toolId}/toolPicture`,
    );
  }

  public getPageableToolDtoList(params: {
    page: number;
    size: number;
    sort?: string;
    sortMode?: 'ASC' | 'DESC';
    term?: string;
  }): Observable<IWHPageableToolDTO> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('size', params.size.toString());
    if (params.sort) {
      httpParams = httpParams.set(
        'sort',
        `${params.sort},${params.sortMode || 'ASC'}`,
      );
    }
    if (params.term) {
      httpParams = httpParams.set('term', params.term);
    }

    return this.httpClient.get<IWHPageableToolDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/tools`,
      { params: httpParams },
    );
  }

  public getToolDtoByToolId(toolID: string): Observable<IWHToolDTO> {
    return this.httpClient.get<IWHToolDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/tools/${toolID}`,
    );
  }

  public createToolByToolDTO(toolDTO: IWHToolDTO): Observable<IWHToolDTO> {
    return this.httpClient.post<IWHToolDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/tools`,
      toolDTO,
    );
  }

  public updateToolByToolDTO(
    toolID: string,
    toolDTO: IWHToolDTO,
  ): Observable<IWHToolDTO> {
    return this.httpClient.post<IWHToolDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/tools/${toolID}`,
      toolDTO,
    );
  }

  public postToolImageFile(
    toolId: string,
    fileBlob: Blob,
  ): Observable<IWHToolDTO> {
    const fileType = fileBlob.type || '';
    const mimeType = fileType || 'application/octet-stream';
    const extension = fileType.startsWith('image/')
      ? `.${fileType.split('/')[1]}`
      : '';

    return this.fileUploadDataService
      .uploadFile(
        {
          apiUrl: environment.apiUrl + FLOW_GATEWAY,
        },
        {
          targetType: 'TOOL',
          targetId: toolId,
        },
        fileBlob,
        {
          fileName: `avatar${extension}`,
          mimeType,
        },
      )
      .pipe(switchMap(() => this.getToolDtoByToolId(toolId)));
  }

  public getToolPreview(
    httpParams: HttpParams,
  ): Observable<IWHToolsPreviewDTO> {
    return this.httpClient.get<IWHToolsPreviewDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/tools/preview`,
      { params: httpParams },
    );
  }
}
